/**
 * 📚 useProgressionLibrary.ts — Hook de Firestore para CRUD de progresiones del usuario
 *
 * Colección: userProgressions/{userId}/items
 * Campos: name, progression, key, bpm, createdAt, tags
 */

import { useCallback, useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../../firebase/config";

export const PROGRESSION_TAGS = [
  "Jazz",
  "Pop",
  "Folclore",
  "Rock",
  "Clásico",
  "Estudio",
] as const;

export type ProgressionTag = (typeof PROGRESSION_TAGS)[number];

export interface SavedProgression {
  id: string;
  name: string;
  progression: string[];
  key: string;
  bpm: number;
  tags: ProgressionTag[];
  createdAt?: Timestamp;
}

export interface CreateProgressionInput {
  name: string;
  progression: string[];
  key: string;
  bpm: number;
  tags?: ProgressionTag[];
}

// ─── Datos de ejemplo precargados ───
export const DEFAULT_PROGRESSIONS: Omit<SavedProgression, "id" | "createdAt">[] = [
  {
    name: "Autumn Leaves",
    progression: ["Cm7", "F7", "Bbmaj7", "Ebmaj7", "Am7b5", "D7", "Gm7", "Gm7"],
    key: "Bb",
    bpm: 90,
    tags: ["Jazz"],
  },
  {
    name: "La Bamba",
    progression: ["C", "F", "G", "F"],
    key: "C",
    bpm: 120,
    tags: ["Folclore"],
  },
  {
    name: "Let It Be",
    progression: ["C", "G", "Am", "F", "C", "G", "F", "C"],
    key: "C",
    bpm: 72,
    tags: ["Pop"],
  },
  {
    name: "ii-V-I Clásico",
    progression: ["Dm7", "G7", "Cmaj7", "Cmaj7"],
    key: "C",
    bpm: 80,
    tags: ["Estudio", "Jazz"],
  },
];

export function useProgressionLibrary() {
  const [userId, setUserId] = useState<string | undefined>(auth.currentUser?.uid);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUserId(u?.uid);
    });
    return () => unsub();
  }, []);

  const [items, setItems] = useState<SavedProgression[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const getCollectionRef = useCallback(() => {
    if (!userId || !db) return null;
    return collection(db, "userProgressions", userId, "items");
  }, [userId]);

  // Cargar progresiones desde Firestore
  const loadProgressions = useCallback(async () => {
    if (!userId) {
      setItems(DEFAULT_PROGRESSIONS.map((p, i) => ({ ...p, id: `default-${i}` })));
      setInitialized(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const colRef = getCollectionRef();
      if (!colRef) {
        setItems(DEFAULT_PROGRESSIONS.map((p, i) => ({ ...p, id: `default-${i}` })));
        return;
      }

      const q = query(colRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<SavedProgression, "id">),
      }));

      if (docs.length === 0) {
        // Fallback a ejemplos si Firestore está vacío
        setItems(DEFAULT_PROGRESSIONS.map((p, i) => ({ ...p, id: `default-${i}` })));
      } else {
        setItems(docs);
      }
    } catch (err) {
      console.error("Error cargando progresiones:", err);
      setError("No se pudieron cargar las progresiones.");
      setItems(DEFAULT_PROGRESSIONS.map((p, i) => ({ ...p, id: `default-${i}` })));
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [userId, getCollectionRef]);

  // Auto-cargar al montar
  useEffect(() => {
    if (!initialized) {
      loadProgressions();
    }
  }, [initialized, loadProgressions]);

  // Guardar progresión
  const saveProgression = useCallback(
    async (input: CreateProgressionInput): Promise<string | null> => {
      if (!userId || !db) {
        // Modo offline: agregar a estado local con ID temporal
        const tempId = `local-${Date.now()}`;
        setItems((prev) => [
          {
            id: tempId,
            ...input,
            tags: input.tags ?? [],
            createdAt: undefined,
          },
          ...prev,
        ]);
        return tempId;
      }

      try {
        const colRef = getCollectionRef();
        if (!colRef) return null;

        const docRef = await addDoc(colRef, {
          ...input,
          tags: input.tags ?? [],
          createdAt: serverTimestamp(),
        });

        const newItem: SavedProgression = {
          id: docRef.id,
          ...input,
          tags: input.tags ?? [],
        };
        setItems((prev) => [newItem, ...prev]);
        return docRef.id;
      } catch (err) {
        console.error("Error guardando progresión:", err);
        setError("No se pudo guardar la progresión.");
        return null;
      }
    },
    [userId, getCollectionRef]
  );

  // Eliminar progresión
  const deleteProgression = useCallback(
    async (id: string) => {
      if (id.startsWith("default-")) {
        setItems((prev) => prev.filter((p) => p.id !== id));
        return;
      }

      if (!userId || !db) {
        setItems((prev) => prev.filter((p) => p.id !== id));
        return;
      }

      try {
        await deleteDoc(doc(db, "userProgressions", userId, "items", id));
        setItems((prev) => prev.filter((p) => p.id !== id));
      } catch (err) {
        console.error("Error eliminando progresión:", err);
        setError("No se pudo eliminar la progresión.");
      }
    },
    [userId]
  );

  // Actualizar progresión
  const updateProgression = useCallback(
    async (id: string, updates: Partial<CreateProgressionInput>) => {
      setItems((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, ...updates, tags: updates.tags ?? p.tags }
            : p
        )
      );

      if (!userId || !db || id.startsWith("default-") || id.startsWith("local-")) {
        return;
      }

      try {
        const docRef = doc(db, "userProgressions", userId, "items", id);
        await updateDoc(docRef, {
          ...updates,
          tags: updates.tags ?? [],
        });
      } catch (err) {
        console.error("Error actualizando progresión:", err);
        setError("No se pudo actualizar la progresión.");
      }
    },
    [userId]
  );

  return {
    items,
    loading,
    error,
    saveProgression,
    deleteProgression,
    updateProgression,
    loadProgressions,
  };
}
